import React, { useContext, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dimmer, Label, LabelDetail, Popup, SemanticWIDTHS, Table } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import cloneDeep from 'lodash.clonedeep'
import { StyledFirstColumn } from '../style'
import CustomIcon from '~/components/CustomIcon'
import { formatNumber } from '~/utils/helpers'
import { getSumOfFilledFeeds, prepareDataForPayload } from '../../utils'
import { StyledEnergyWrapper, StyledLoader, StyledTable } from './style'
import { FeedPageContext } from '../..'
import { useParams } from 'react-router-dom'
import InfoPopup from '~/components/InfoPopup'
import { CO2OptionsType } from '~/api/services/co2'
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import BarleyModal from '../TwelveStep/BarleyModal'

export const monthsArray = [
  { key: 1, label: '1 Monat' },
  { key: 2, label: '2 Monate' },
  { key: 3, label: '3 Monate' },
  { key: 4, label: '4 Monate' },
  { key: 5, label: '5 Monate' },
  { key: 6, label: '6 Monate' },
  { key: 7, label: '7 Monate' },
  { key: 8, label: '8 Monate' },
  { key: 9, label: '9 Monate' },
  { key: 10, label: '10 Monate' },
  { key: 11, label: '11 Monate' }
];

interface Props {
  milkOptions: CO2OptionsType
}

const ThirteenStep: React.FC<Props> = (props) => {
  const { milkOptions } = props
  const { control, getValues, setValue } = useFormContext()
  const { id } = useParams()
  const { t } = useTranslation()

  const { energyCalcData, setEnergyCalcData, loadingEnergyData, setIsEnergyDataFetching } = useContext(FeedPageContext)

  const panesOptions = [
    {
      title: t('Coarse Feeds'),
      key: 'coarseFeeds'
    },
    {
      title: t('Power and mineral feed'),
      key: 'concentratedFeeds'
    }
  ]

  const rearingPhases = useWatch({ control, name: 'rearingPhases' })
  const watchRearingPhasesType = useWatch({ control, name: 'rearingPhasesType' })

  const isDlgFeed = watchRearingPhasesType === 'dlg'

  const {
    data: energyData,
    isFetching: isFetchingData
  } = CO2ClimateQuery.getClimaChEnergyData(
    id,
    'heifers',
    { ...prepareDataForPayload(getValues()) }
  )

  useEffect(() => {
    setIsEnergyDataFetching(isFetchingData)
  }, [isFetchingData])

  useEffect(() => {
    if (energyCalcData) {
      setEnergyCalcData(null)
    }
  }, [rearingPhases])

  const [parsedData, setParsedData] = useState(null)
  const [barleyData, setBarleyData] = useState(null)

  function getParsedData(data) {
    const result = {
      coarseFeeds: [],
      concentratedFeeds: []
    };

    data.forEach(item => {
      item.concentratedFeeds.forEach(feed => {
        if (!result.concentratedFeeds.find(item => item.id === feed.id)) {
          const concentratedFeeds = getValues('calvesConcentratedFeeds')
          const index = concentratedFeeds?.findIndex(concentratedFeed => concentratedFeed.id === feed.id)
          result.concentratedFeeds.push({
            ...feed,
            dryMatter: concentratedFeeds[index]?.dryMatter,
            energy: concentratedFeeds[index]?.energy,
            rawAsh: concentratedFeeds[index]?.rawAsh,
            rawProtein: concentratedFeeds[index]?.rawProtein,
            rawFat: concentratedFeeds[index]?.rawFat,
            rawFiber: concentratedFeeds[index]?.rawFiber,
            index
          });
        }
      });
      item.coarseFeeds.forEach(feed => {
        if (!result.coarseFeeds.find(item => item.id === feed.id)) {
          const coarseFeeds = getValues('calvesCoarseFeeds')
          const index = coarseFeeds?.findIndex(coarseFeed => coarseFeed.id === feed.id)
          result.coarseFeeds.push({
            ...feed,
            dryMatter: coarseFeeds[index]?.dryMatter,
            energy: coarseFeeds[index]?.energy,
            rawAsh: coarseFeeds[index]?.rawAsh,
            rawProtein: coarseFeeds[index]?.rawProtein,
            rawFat: coarseFeeds[index]?.rawFat,
            rawFiber: coarseFeeds[index]?.rawFiber,
            index
          });
        }
      });
    });
    return result
  }

  useEffect(() => {
    setParsedData(getParsedData(cloneDeep(rearingPhases)))
  }, [])

  function renderTableHeaders(firstColumnTitle) {
    return <Table.Row>
      <Table.HeaderCell width={'4'}>{firstColumnTitle}</Table.HeaderCell>
      {rearingPhases.map(phase => {
        return <Table.HeaderCell key={phase.name}>
          {`${phase?.name || ''} (${phase.period}${isDlgFeed ? 'Monate' : ''})`}
        </Table.HeaderCell>
      })}
    </Table.Row>
  }

  function getCowsFeedKey(key: string) {
    switch (key) {
      case 'coarseFeeds':
        return 'calvesCoarseFeeds';
      case 'concentratedFeeds':
        return 'calvesConcentratedFeeds';
      default:
        return 'calvesCoarseFeeds'
    }
  }

  function renderTextValues(name) {
    const value = formatNumber(getValues(name), 2)
    return !isNaN(parseFloat(value)) && <div style={{ padding: '.47857143em 0.7em' }}>
      {`${value} kg FM/Tier u. Tag`}
    </div>
  }

  function renderInputValue(disabled, indexFeed, name, feedData) {
    if (!disabled) {
      return <FormFields.Input
        disabled={indexFeed < 0}
        placeholder={t('kg FM/animal and day')}
        name={name}
        control={control}
        typeInput={'controlled-inline-error'}
        withoutAsterisk
        rules={{
          required: true,
          validate: (value) => {
            const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
            if(feedData?.limitMaxValue && transformValue > feedData?.limitMaxValue) {
              return `Der bei ${feedData.label} eingegebene Wert liegt oberhalb des Maximalwerts von ${formatNumber(feedData.limitMaxValue, 2)} kg. Bitte korrigieren Sie Ihre Eingabe.`
            }
            return true
          }
        }}
        warningMax={feedData?.warningMaxValue ? {
          value: feedData?.warningMaxValue,
          message: `Der bei ${feedData.label} eingegebene Wert liegt außerhalb des Normalbereichs von maximal ${formatNumber(feedData?.warningMaxValue, 2)} kg. 
          Wenn Sie sich sicher sind, dass Ihre Eingabe korrekt ist, können Sie diese Meldung ignorieren.`
        } : null}
        suffix={' kg FM/Tier u. Tag'}
        noErrorMessage
        allowDecimals
        decimalScale={2}
      />
    }
    return
  }

  function renderTableBody(rows, key) {
    return <Table.Body>
      {rows.map((feed) => {
        const cowsFeedKey = getCowsFeedKey(key)
        const feedData = getValues(cowsFeedKey)[feed.index]
        return <Table.Row key={`${cowsFeedKey}-${feed.id}`}>
          <Table.Cell>
            <div>{feed.label}</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
              <small style={{ fontSize: '85%' }}>
                {t('[TM: {{dryMatter}} % Energie: {{energy}} MJ NEL/kg TM]...',
                  {
                    dryMatter: formatNumber(getValues(cowsFeedKey)[feed.index]?.dryMatter?.replace(',', '.'), 2),
                    energy: formatNumber(getValues(cowsFeedKey)[feed.index]?.energy?.replace(',', '.'), 2)
                  }
                )}
              </small>
              {feed?.dryMatter && feed?.energy && <div style={{ display: 'flex', gap: 5, marginLeft: 7 }}>
                <InfoPopup
                  position={'top center'}
                  styleIcon={{ fontSize: '1.2em' }}
                  content={<small style={{ fontSize: '85%' }}>
                    <div>{`TM: ${formatNumber(feedData?.dryMatter?.replace(',', '.'), 2)} %`}</div>
                    <div>{`Energie: ${formatNumber(feedData?.energy?.replace(',', '.'), 2)} MJ NEL/kg TM`}</div>
                    <div>{`Rohasche: ${formatNumber(feedData?.rawAsh?.replace(',', '.') || 0, 2)} g/kg TM`}</div>
                    <div>{`Rohprotein: ${formatNumber(feedData?.rawProtein?.replace(',', '.') || 0, 2)} g/kg TM`}</div>
                    <div>{`Rohfett: ${formatNumber(feedData?.rawFat?.replace(',', '.') || 0, 2)} g/kg TM`}</div>
                    <div>{`Rohfaser: ${formatNumber(feedData?.rawFiber?.replace(',', '.') || 0, 2)} g/kg TM`}</div>
                  </small>}
                />
                {!isDlgFeed && <Popup
                  content={<div>
                    <div>{t('Change')}</div>
                  </div>}
                  trigger={<CustomIcon
                    name={'pencil'}
                    onClick={() => {
                      const barleyValue = getValues(`${cowsFeedKey}[${feed.index}]`)
                      setBarleyData({
                        ...feedData,
                        ...barleyValue,
                        index: feed.index,
                        key: cowsFeedKey,
                        feedName: feed.label,
                        isJuicyFeed: false
                      })
                    }}
                    style={{ fontSize: '1.2em' }}
                  />}
                />}
              </div>}
            </div>
          </Table.Cell>
          {rearingPhases.map((phase, index) => {
            const indexFeed = phase[key].findIndex(value => value.id === feed.id)
            const name = `rearingPhases[${index}].${key}[${indexFeed}].value`
            const disabled = indexFeed < 0

            return <Table.Cell width={Math.floor(12 / rearingPhases.length) as SemanticWIDTHS} key={key}>
              {isDlgFeed ? renderTextValues(name) : renderInputValue(disabled, indexFeed, name, feedData)}
            </Table.Cell>
          })}
        </Table.Row>
      })}
    </Table.Body>
  }

  function renderTable(rows, key, firstColumnTitle) {
    return rows?.length > 0 && <StyledTable>
      <Table.Header>
        {renderTableHeaders(firstColumnTitle)}
      </Table.Header>
      {renderTableBody(rows, key)}
    </StyledTable>
  }

  function parseConcentratedFeeds(concentratedFeeds) {
    return concentratedFeeds.reduce((acc, item) => {
      const { group } = item;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    }, {});
  }

  function getConcentratedFeedTitle(groupIndex) {
    return Object.keys(milkOptions.cowsConcentratedFeeds)[groupIndex - 1]
  }

  function renderFeedRations() {
    return Object.keys(parsedData).map(key => {
      const title = panesOptions.find(pane => pane.key === key)?.title
      if (key === 'concentratedFeeds') {
        const groupConcentratedFeeds = parseConcentratedFeeds(parsedData?.concentratedFeeds)
        return <div style={{ margin: '1em 0em' }} key={key}>
          <div>
            {Object.keys(groupConcentratedFeeds).map(key => {
              const firstColumnTitle = <StyledFirstColumn>
                <div>{title}</div>
                <div className='subHeader'>{getConcentratedFeedTitle(key)}</div>
              </StyledFirstColumn>
              return <div style={{ margin: '1em 0em' }} key={groupConcentratedFeeds[key]}>
                {renderTable(Object.values(groupConcentratedFeeds[key]), 'concentratedFeeds', firstColumnTitle)}
              </div>
            })}
          </div>
        </div>
      } else {
        return <div style={{ margin: '1em 0em' }} key={key}>
          {renderTable(Object.values(parsedData[key]), key, title)}
        </div>
      }
    })
  }

  function renderBarleyModal() {
    return <BarleyModal
      barleyData={barleyData}
      handleClose={(data) => {
        if(data && barleyData) {
          const value = getValues(`${barleyData?.key}[${barleyData.index}]`)
          setValue(`${barleyData?.key}[${barleyData.index}]`, { ...value, ...data })
        }
        setBarleyData(null)
      }}
    />
  }

  function renderTotal() {
    const sums = rearingPhases.map(feedGroup => getSumOfFilledFeeds(feedGroup))
    return <StyledTable>
      <Table.Header>
        <Table.HeaderCell width={'4'}>{t('Total')}</Table.HeaderCell>
        {sums.map((sum) => {
          return <Table.Cell width={Math.floor(12 / rearingPhases.length) as SemanticWIDTHS} key={`sum${Math.random()}`}>
            <div style={{ marginLeft: 14 }}>
              {`${formatNumber(sum, 2)} kg FM/Tier u. Tag`}
            </div>
          </Table.Cell>
        })}
      </Table.Header>
    </StyledTable>
  }

  function renderSpinner() {
    return <Dimmer inverted active={true} style={{ background: 'transparent' }}>
      <StyledLoader active={true} />
    </Dimmer>
  }

  function renderTotalEnergy() {
    return <StyledEnergyWrapper>
      {energyCalcData?.totalEnergyWithAddition && <Label as='a'>
        {t('Calculated feed energy')}:
        <LabelDetail style={{ position: 'relative', minWidth: '1.3rem' }}>
          {loadingEnergyData ? renderSpinner() : `${formatNumber(energyCalcData?.totalEnergyWithAddition, 0)} MJ NEL/Jahr`}
        </LabelDetail>
      </Label>}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Label as='a'>
          {t('Total energy requirement')}:
          <LabelDetail style={{ position: 'relative', minWidth: '1.3rem' }}>
            {isFetchingData ? renderSpinner() : `${formatNumber(energyData?.youngCattleTotalEnergy, 0)} MJ NEL/Jahr`}
          </LabelDetail>
        </Label></div>
    </StyledEnergyWrapper>
  }

  return <div>
    {renderTotalEnergy()}
    <div>
      {parsedData && renderFeedRations()}
      {renderTotal()}
      {renderBarleyModal()}
    </div>
  </div>
}
export default ThirteenStep